import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/config/routes';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';

interface RouteGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

const REDIRECT_STORAGE_KEY = 'auth_redirect_to';

export const RouteGuard: React.FC<RouteGuardProps> = ({ 
  children, 
  requireAuth = false,
  redirectTo = ROUTES.PERMIT_BENCHMARKING.ROOT
}) => {
  const { user, status, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Store redirect intent when user tries to access protected route
  useEffect(() => {
    if (requireAuth && !isLoading && !user && status !== 'loading') {
      const currentPath = location.pathname + location.search;
      // Don't store login page as redirect target
      if (currentPath !== ROUTES.LOGIN && currentPath !== ROUTES.RESET_PASSWORD) {
        sessionStorage.setItem(REDIRECT_STORAGE_KEY, currentPath);
        console.log('[RouteGuard] Stored redirect intent:', currentPath);
      }
    }
  }, [requireAuth, isLoading, user, status, location]);

  // Handle redirect after successful authentication
  useEffect(() => {
    if (!isLoading && user && status === 'authenticated' && isRedirecting) {
      const storedRedirect = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
      
      if (storedRedirect) {
        sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
        console.log('[RouteGuard] Redirecting to stored path:', storedRedirect);
        navigate(storedRedirect, { replace: true });
      } else {
        console.log('[RouteGuard] No stored redirect, going to default:', redirectTo);
        navigate(redirectTo, { replace: true });
      }
      
      setIsRedirecting(false);
    }
  }, [isLoading, user, status, isRedirecting, navigate, redirectTo]);

  // Show loading state while auth is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // If auth is required but user is not authenticated, redirect to login
  if (requireAuth && !user && status !== 'loading') {
    console.log('[RouteGuard] User not authenticated, redirecting to login');
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // If user is authenticated and we're on login page, redirect to intended destination
  if (user && status === 'authenticated' && location.pathname === ROUTES.LOGIN) {
    const storedRedirect = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
    const targetPath = storedRedirect || redirectTo;
    
    if (storedRedirect) {
      sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
    }
    
    console.log('[RouteGuard] User authenticated on login page, redirecting to:', targetPath);
    return <Navigate to={targetPath} replace />;
  }

  return <>{children}</>;
};

// Hook to get stored redirect path
export const useStoredRedirect = () => {
  const getStoredRedirect = () => sessionStorage.getItem(REDIRECT_STORAGE_KEY);
  const clearStoredRedirect = () => sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
  const setStoredRedirect = (path: string) => sessionStorage.setItem(REDIRECT_STORAGE_KEY, path);
  
  return {
    getStoredRedirect,
    clearStoredRedirect,
    setStoredRedirect,
  };
};
